// src/components/LoadHistory.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import {
  FaTrashAlt,
  FaUndoAlt,
  FaTimes,
  FaTimesCircle,
  FaRegCalendarAlt,
  FaMapMarkerAlt,
  FaArrowRight,
  FaCheckCircle,
  FaEye,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";

import { Truck, User, Container } from "lucide-react";
import "../styles/LoadHistory.css";
import LoadHistoryDetails from "./LoadHistoryDetails";
import BillingReviewAction from "./BillingReviewAction";
import Select from "react-select";
import { customSelectStyles } from "../styles/reactSelectStyles";
import { getSocket } from "../lib/socket";
import { useTranslation } from "react-i18next";

/* ===================== Helpers globais ===================== */

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime?.() ?? d)) return dateStr; // fallback
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDDMM = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime?.() ?? d)) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
};

const formatDateDots = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime?.() ?? d)) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const fmtMMDD = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime?.() ?? d)) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}`;
};

const defaultFilters = {
  status: [],
  clientNames: [],
  carrierNames: [],
  dateFrom: "",
  dateTo: "",
  hourFrom: "",
  hourTo: "",
  priceMin: "",
  priceMax: "",
  lengthMin: "",
  lengthMax: "",
  widthMin: "",
  widthMax: "",
  heightMin: "",
  heightMax: "",
  pickupIncludes: "",
  deliveryIncludes: "",
  loadId: "",
  sessionId: "",
};

// 🟢 NEW (uses denormalized snapshots, keeps legacy fallback)
const collectFilterOptions = (histories = []) => {
  const clients = new Set();
  const carriers = new Set();
  const pickups = new Set();
  const deliveries = new Set();

  histories.forEach((h) => {
    const load = h.load || {};

    const clientName =
      h?.client ||
      load?.industryClient?.name ||
      load?.industryClient?.companyName ||
      load?.carrierClient?.name ||
      load?.carrierClient?.companyName ||
      null;
    if (clientName) clients.add(clientName);

    const carrierName =
      load?.carrier?.name ||
      load?.carrier?.companyName ||
      load?.carrierName ||
      h?.carrierCompanyName ||
      null;
    if (carrierName) carriers.add(carrierName);

    if (h?.pickupLocation) pickups.add(h.pickupLocation);
    if (h?.deliveryLocation) deliveries.add(h.deliveryLocation);

    (load?.pickupDeliveries || []).forEach((pd) => {
      if (!pd?.location) return;
      if (pd.type === "PICKUP") pickups.add(pd.location);
      if (pd.type === "DELIVERY") deliveries.add(pd.location);
    });
  });

  return {
    clientOptions: Array.from(clients).sort(),
    carrierOptions: Array.from(carriers).sort(),
    pickupOptions: Array.from(pickups).sort(),
    deliveryOptions: Array.from(deliveries).sort(),
  };
};

const uniquePlates = (arr = []) => {
  const s = new Set(arr.filter(Boolean).map((p) => String(p).trim()));
  return Array.from(s);
};

// Extract just the plate if a legacy "AA-00-BB • Brand" string slips in.
const onlyPlate = (s) => String(s ?? "").split("•")[0].trim();

const isAggregateAssetLabel = (value) => {
  const s = String(value ?? "").trim().toLowerCase();

  return (
    /^\d+\s*trucks?$/.test(s) ||
    /^\d+\s*camiões?$/.test(s) ||
    /^\d+\s*camioes?$/.test(s) ||
    /^\d+\s*trailers?$/.test(s) ||
    /^\d+\s*reboques?$/.test(s)
  );
};

const normalizePlate = (value) => {
  const s = onlyPlate(value);

  if (!s || s === "—") return "";
  if (isAggregateAssetLabel(s)) return "";

  return s
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();
};

const uniqueNormalizedPlates = (arr = []) => {
  const seen = new Set();
  const result = [];

  arr.forEach((value) => {
    const normalized = normalizePlate(value);
    if (!normalized || seen.has(normalized)) return;

    seen.add(normalized);
    result.push(onlyPlate(value));
  });

  return result;
};

const isAggregateDriverLabel = (value) => {
  const s = String(value ?? "").trim().toLowerCase();

  return (
    /^\d+\s*drivers?$/.test(s) ||
    /^\d+\s*motoristas?$/.test(s) ||
    /^\d+\s*chauffeurs?$/.test(s) ||
    /^\d+\s*conductores?$/.test(s)
  );
};

const cleanDriverName = (value) => {
  const s = String(value ?? "").trim();

  if (!s || s === "—") return "";
  if (isAggregateDriverLabel(s)) return "";

  return s;
};

const getDriverDisplayName = (...values) => {
  for (const value of values) {
    const cleaned = cleanDriverName(value);
    if (cleaned) return cleaned;
  }

  return "—";
};

const fmtPointDim = (v) => {
  if (v === null || v === undefined || v === "") return "—";

  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);

  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const hasPointDimensions = (point) => {
  return point?.length != null || point?.width != null || point?.height != null;
};

const formatPointDimensions = (point) => {
  if (!hasPointDimensions(point)) return "—";

  return `${fmtPointDim(point.length)} × ${fmtPointDim(point.width)} × ${fmtPointDim(point.height)} m`;
};

const inRange = (value, min, max) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return true;
  const num = Number(value);
  if (min !== "" && Number.isFinite(Number(min)) && num < Number(min)) return false;
  if (max !== "" && Number.isFinite(Number(max)) && num > Number(max)) return false;
  return true;
};

const hhmmToMin = (s) => {
  if (!s) return null;
  const [h, m] = String(s).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const timeWithin = (hhmm, from, to) => {
  if (!hhmm || (!from && !to)) return true;
  const v = hhmmToMin(hhmm);
  if (v == null) return true;
  const f = hhmmToMin(from);
  const t = hhmmToMin(to);
  if (f != null && v < f) return false;
  if (t != null && v > t) return false;
  return true;
};

const dateWithin = (dateStr, from, to) => {
  if (!dateStr || (!from && !to)) return true;
  const v = new Date(dateStr).setHours(0, 0, 0, 0);
  if (Number.isNaN(v)) return true;
  if (from) {
    const f = new Date(from).setHours(0, 0, 0, 0);
    if (v < f) return false;
  }
  if (to) {
    const t = new Date(to).setHours(0, 0, 0, 0);
    if (v > t) return false;
  }
  return true;
};

/* ===================== Export helpers ===================== */

const compactStop = (location, date, hour) => {
  const loc = location || "—";
  const dt = date ? formatDate(date) : "";
  const tm = hour || "";
  return [loc, [dt, tm].filter(Boolean).join(" ")].filter(Boolean).join("\n");
};

const flattenRowForPdf = (row) => {
  const load = row.load || {};

  const clientName =
    row.client ||
    load.industryClient?.name ||
    load.industryClient?.companyName ||
    load.carrierClient?.name ||
    load.carrierClient?.companyName ||
    "—";

  const carrierName =
    load.carrier?.name ||
    load.carrier?.companyName ||
    load.carrierName ||
    row.carrierCompanyName ||
    "—";

  const priceRaw =
    row.price != null && row.price !== ""
      ? row.price
      : typeof load.price === "number" || typeof load.price === "string"
      ? load.price
      : null;

  const priceText =
    priceRaw == null || priceRaw === "" || Number.isNaN(Number(priceRaw))
      ? "—"
      : `${Number(priceRaw).toFixed(2)} €`;

  const L = row.merchLength ?? load.length ?? "—";
  const W = row.merchWidth ?? load.width ?? "—";
  const H = row.merchHeight ?? load.height ?? "—";

  return {
    session: row.transportSessionNumber ?? "—",
    load: row.loadNumber ?? load.loadNumber ?? "—",
    status: String(row.status || load.status || "—"),
    client: clientName,
    carrier: carrierName,
    price: priceText,
    size: `${L} × ${W} × ${H} m`,
    pickup: compactStop(row.pickupLocation, row.pickupDate, row.pickupHour),
    delivery: compactStop(row.deliveryLocation, row.deliveryDate, row.deliveryHour),
    stops: `${row.pickupCount ?? 0}P / ${row.deliveryCount ?? 0}D`,
  };
};

const flattenRowForExport = (row, { includeExtras = false } = {}) => {
  const load = row.load || {};

  const pickStr = [
    row.pickupLocation,
    row.pickupHour,
    row.pickupDate ? formatDate(row.pickupDate) : "",
    row.truckPickupInfo,
    row.trailerPickupInfo,
    row.driverPickupInfo,
  ]
    .filter(Boolean)
    .join(" | ");

  const delStr = [
    row.deliveryLocation,
    row.deliveryHour,
    row.deliveryDate ? formatDate(row.deliveryDate) : "",
    row.truckDeliveryInfo,
    row.trailerDeliveryInfo,
    row.driverDeliveryInfo,
  ]
    .filter(Boolean)
    .join(" | ");

  const clientName =
    row.client ||
    load.industryClient?.name ||
    load.industryClient?.companyName ||
    load.carrierClient?.name ||
    load.carrierClient?.companyName ||
    "";

  const carrierName =
    load.carrier?.name ||
    load.carrier?.companyName ||
    load.carrierName ||
    row.carrierCompanyName ||
    "";

  const base = {
    transportSessionNumber: row.transportSessionNumber ?? "",
    loadNumber: row.loadNumber ?? load.loadNumber ?? "",
    status: String(row.status || load.status || ""),
    client: clientName,
    carrier: carrierName,
    carrierId: row.carrierId ?? "",
    price:
      row.price != null && row.price !== ""
        ? String(row.price)
        : typeof load.price === "number" || typeof load.price === "string"
        ? String(load.price)
        : "",
    length: row.merchLength ?? load.length ?? "",
    width: row.merchWidth ?? load.width ?? "",
    height: row.merchHeight ?? load.height ?? "",
    firstPickup: row.pickupDate
      ? `${row.pickupHour || ""} ${formatDate(row.pickupDate)}`.trim()
      : "",
    lastDelivery: row.deliveryDate
      ? `${row.deliveryHour || ""} ${formatDate(row.deliveryDate)}`.trim()
      : "",
    pickups: pickStr,
    deliveries: delStr,
    pickupCount: row.pickupCount ?? "",
    deliveryCount: row.deliveryCount ?? "",
  };

  if (!includeExtras) return base;

  return {
    ...base,
    odometer: load.odometer ?? "",
    OBU: load.OBU ?? "",
    invoiceNumber: load.invoiceNumber ?? row.invoiceNumber ?? "",
    vatAmount: load.vatAmount ?? "",
    vatRate: load.vatRate ?? "",
  };
};

const toCSV = (rows) => {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = (v ?? "").toString();
    const needsQuotes = /[",\n;]|^\s|\s$/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ];
  return lines.join("\n");
};

const download = (filename, mime, content) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/* ===================== Badges de filtros ===================== */

function FilterBadges({ t, filters, onRemove, onClearAll, formatStatusLabel }) {
  const entries = [];

  if (filters.status?.length)
    entries.push({
      k: "status",
      label: t("activeFilters.status", { value: formatStatusLabel(filters.status[0]) }),
    });

  if (filters.clientNames?.length)
    entries.push({
      k: "clientNames",
      label: t("activeFilters.client", { value: filters.clientNames[0] }),
    });

  if (filters.carrierNames?.length)
    entries.push({
      k: "carrierNames",
      label: t("activeFilters.carrier", { value: filters.carrierNames[0] }),
    });

  if (filters.loadId)
    entries.push({ k: "loadId", label: t("activeFilters.loadId", { value: filters.loadId }) });

  if (filters.sessionId)
    entries.push({
      k: "sessionId",
      label: t("activeFilters.sessionId", { value: filters.sessionId }),
    });

  if (filters.pickupIncludes)
    entries.push({
      k: "pickupIncludes",
      label: t("activeFilters.pickupIncludes", { value: filters.pickupIncludes }),
    });

  if (filters.deliveryIncludes)
    entries.push({
      k: "deliveryIncludes",
      label: t("activeFilters.deliveryIncludes", { value: filters.deliveryIncludes }),
    });

  if (filters.dateFrom || filters.dateTo) {
    entries.push({
      k: "dateFromTo",
      compKey: "dateFromTo",
      label: t("activeFilters.dateRange", {
        from: filters.dateFrom || "…",
        to: filters.dateTo || "…",
      }),
    });
  }

  if (filters.hourFrom || filters.hourTo) {
    entries.push({
      k: "hourFromTo",
      compKey: "hourFromTo",
      label: t("activeFilters.hourRange", {
        from: filters.hourFrom || "…",
        to: filters.hourTo || "…",
      }),
    });
  }

  if (filters.priceMin || filters.priceMax)
    entries.push({
      k: "price",
      compKey: "price",
      label: t("activeFilters.priceRange", {
        min: filters.priceMin || "…",
        max: filters.priceMax || "…",
      }),
    });

  if (filters.lengthMin || filters.lengthMax)
    entries.push({
      k: "length",
      compKey: "length",
      label: t("activeFilters.lengthRange", {
        min: filters.lengthMin || "…",
        max: filters.lengthMax || "…",
      }),
    });

  if (filters.widthMin || filters.widthMax)
    entries.push({
      k: "width",
      compKey: "width",
      label: t("activeFilters.widthRange", {
        min: filters.widthMin || "…",
        max: filters.widthMax || "…",
      }),
    });

  if (filters.heightMin || filters.heightMax)
    entries.push({
      k: "height",
      compKey: "height",
      label: t("activeFilters.heightRange", {
        min: filters.heightMin || "…",
        max: filters.heightMax || "…",
      }),
    });

  if (!entries.length) return null;

  return (
    <div className="load-history-active-filters">
      {entries.map((e, i) => (
        <span className="load-history-badge" key={e.compKey || e.k || i}>
          {e.label}
          <button
            className="load-history-badge-x"
            title={t("tooltips.removeFilter")}
            onClick={() => {
              if (e.k === "dateFromTo") {
                onRemove("dateFrom");
                onRemove("dateTo");
                return;
              }
              if (e.k === "hourFromTo") {
                onRemove("hourFrom");
                onRemove("hourTo");
                return;
              }
              if (e.k === "price") {
                onRemove("priceMin");
                onRemove("priceMax");
                return;
              }
              if (e.k === "length") {
                onRemove("lengthMin");
                onRemove("lengthMax");
                return;
              }
              if (e.k === "width") {
                onRemove("widthMin");
                onRemove("widthMax");
                return;
              }
              if (e.k === "height") {
                onRemove("heightMin");
                onRemove("heightMax");
                return;
              }
              onRemove(e.k);
            }}
            type="button"
          >
            <FaTimes />
          </button>
        </span>
      ))}
      <button className="load-history-clear-all" onClick={onClearAll} type="button">
        {t("actions.clearAll")}
      </button>
    </div>
  );
}

/* ===================== Export Modal ===================== */

function ExportModal({ t, open, onClose, onConfirm, defaultFrom = "", defaultTo = "" }) {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [includeExtras, setIncludeExtras] = useState(false);
  const [format, setFormat] = useState("xls"); // 'xls' | 'csv' | 'pdf'

  useEffect(() => {
    if (open) {
      setFrom(defaultFrom || "");
      setTo(defaultTo || "");
      setIncludeExtras(false);
      setFormat("xls");
    }
  }, [open, defaultFrom, defaultTo]);

  if (!open) return null;

  return (
    <div
      className="export-modal-backdrop"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 120,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="export-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: 620,
          maxWidth: "95vw",
          background: "#0f1115",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 14,
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
          padding: 20,
          color: "#E8ECF3",
        }}
      >
        <h3 style={{ margin: "4px 0 12px 0", fontSize: 20 }}>{t("export.title")}</h3>

        <div
          className="export-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            marginBottom: 6,
          }}
        >
          <div>
            <label style={{ display: "block", fontSize: 13, opacity: 0.85 }}>{t("export.dateFrom")}</label>
            <div style={{ position: "relative" }}>
              <input
                type="date"
                className="load-history-input"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                style={{ width: "100%", paddingRight: 32 }}
              />
              <FaRegCalendarAlt
                style={{
                  position: "absolute",
                  right: 8,
                  top: "40%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  opacity: 0.6,
                }}
              />
            </div>
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>{from ? formatDateDots(from) : ""}</div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: 13, opacity: 0.85 }}>{t("export.dateTo")}</label>
            <div style={{ position: "relative" }}>
              <input
                type="date"
                className="load-history-input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                style={{ width: "100%", paddingRight: 32 }}
              />
              <FaRegCalendarAlt
                style={{
                  position: "absolute",
                  right: 8,
                  top: "40%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  opacity: 0.6,
                }}
              />
            </div>
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>{to ? formatDateDots(to) : ""}</div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 6 }}>{t("export.format")}</div>

          <div style={{ display: "grid", gap: 8 }}>
            {[
              { value: "csv", label: t("export.formats.csv") },
              { value: "xls", label: t("export.formats.xls") },
              { value: "pdf", label: t("export.formats.pdf") },
            ].map((opt) => (
              <div
                key={opt.value}
                onClick={() => setFormat(opt.value)}
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: format === opt.value ? "1px solid #019dee" : "1px solid #444",
                  background: format === opt.value ? "#444" : "#1b1b1b",
                  cursor: "pointer",
                  color: format === opt.value ? "#fff" : "#bbb",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s",
                }}
              >
                <span>{opt.label}</span>
                {format === opt.value && <span style={{ fontSize: 12, color: "#019dee" }}>✓</span>}
              </div>
            ))}
          </div>
        </div>

        {includeExtras ? null : null}

        <div
          className="export-actions"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 18,
          }}
        >
          <button className="load-history-btn" onClick={onClose} type="button">
            {t("actions.cancel")}
          </button>
          <button
            className="load-history-btn load-history-btn-primary"
            onClick={() => onConfirm({ from, to, includeExtras, format })}
            type="button"
          >
            {t("actions.export")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================== Componente principal ===================== */

function LoadHistory({ searchTerm = "", onReverted, onVisibleCountChange }) {
  const { t, i18n } = useTranslation("load_history");

  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);

  const refreshBillingState = () => {
    setBillingRefreshKey((previous) => previous + 1);
  };

  // Detalhes
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);

  // Filtros
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState({ ...defaultFilters });
  const [draftFilters, setDraftFilters] = useState({ ...defaultFilters });

  // Export
  const [showExportModal, setShowExportModal] = useState(false);

  /* ===================== Pagination by transport sessions ===================== */
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [collapsedSessions, setCollapsedSessions] = useState({});

  const formatStatusLabel = (s) => {
    const v = String(s || "").toUpperCase();
    if (v === "COMPLETED") return t("status.completed");
    if (v === "PENDING") return t("status.pending");
    if (v === "CANCELLED") return t("status.cancelled");
    return s || "—";
  };

  const getHistoryStatus = (row) => {
    const load = row?.load || {};
    return String(load.status || row.status || "").toUpperCase();
  };

  const getSessionKey = (row) => {
    const load = row?.load || {};
    return String(
      row.transportSessionNumber ??
        row.transportSessionId ??
        load.transportsessionId ??
        load.transportSessionNumber ??
        "without-session"
    );
  };

  const groupHistoriesBySession = (list = []) => {
    const map = new Map();

    list.forEach((row) => {
      const key = getSessionKey(row);

      if (!map.has(key)) {
        map.set(key, {
          key,
          transportSessionNumber:
            row.transportSessionNumber ??
            row.transportSessionId ??
            row.load?.transportsessionId ??
            "—",
          rows: [],
        });
      }

      map.get(key).rows.push(row);
    });

    return Array.from(map.values()).map((session) => {
      const totalLoads = session.rows.length;

      const completedLoads = session.rows.filter(
        (row) => getHistoryStatus(row) === "COMPLETED"
      ).length;

      const cancelledLoads = session.rows.filter(
        (row) => getHistoryStatus(row) === "CANCELLED"
      ).length;

      const sessionStatus =
        totalLoads > 0 && completedLoads === totalLoads
          ? "COMPLETED"
          : totalLoads > 0 && cancelledLoads === totalLoads
          ? "CANCELLED"
          : "PENDING";

      const progress =
        totalLoads > 0 && sessionStatus === "COMPLETED"
          ? Math.round((completedLoads / totalLoads) * 100)
          : 0;

      return {
        ...session,
        status: sessionStatus,
        totalLoads,
        completedLoads,
        cancelledLoads,
        progress,
      };
    });
  };

  const toggleSessionCollapse = (sessionKey) => {
    setCollapsedSessions((prev) => ({
      ...prev,
      [sessionKey]: !prev[sessionKey],
    }));
  };

  // Histórico operacional e estado fiscal confirmado pelo backend.
  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    async function fetchHistory() {
      try {
        const { data } = await axios.get(
          "/load-history/summary",
          { signal: controller.signal }
        );

        if (!active) return;

        const rows = Array.isArray(data?.histories)
          ? data.histories
          : Array.isArray(data)
            ? data
            : [];

        setHistories(rows);
      } catch (error) {
        if (!active) return;

        console.error(
          "❌ Failed to fetch history summary:",
          error
        );

        // Apenas no carregamento inicial usamos o endpoint
        // alternativo. Numa atualização fiscal, não substituímos
        // o estado fiscal por dados sem confirmação.
        if (billingRefreshKey > 0) {
          return;
        }

        try {
          const { data } = await axios.get(
            "/load-history",
            { signal: controller.signal }
          );

          if (!active) return;

          setHistories(
            Array.isArray(data) ? data : []
          );
        } catch (fallbackError) {
          if (!active) return;

          if (fallbackError?.response?.status === 429) {
            alert(t("errors.tooManyRequests"));
          }

          console.error(
            "❌ Failed to fetch history:",
            fallbackError
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    fetchHistory();

    return () => {
      active = false;
      controller.abort();
    };
  }, [billingRefreshKey]);

  // 🔴 NEW — Live updates (tenant-scoped list)
  useEffect(() => {
    const s = getSocket();

    const subscribe = () => {
      s.emit("subscribe:loadhistory");
    };

    subscribe();
    s.on("connect", subscribe);
    s.on("reconnect", subscribe);

    const onCreated = (row) =>
      setHistories((prev) =>
        prev.some((x) => String(x.id) === String(row.id)) ? prev : [row, ...prev]
      );

    const onRevertedSocket = ({ id }) =>
      setHistories((prev) => prev.filter((x) => String(x.id) !== String(id)));

    const onDeleted = ({ id }) =>
      setHistories((prev) => prev.filter((x) => String(x.id) !== String(id)));

    const onUpdated = (row) =>
      setHistories((prev) =>
        prev.map((x) => (String(x.id) === String(row.id) ? { ...x, ...row } : x))
      );

    s.on("loadhistory:created", onCreated);
    s.on("loadhistory:reverted", onRevertedSocket);
    s.on("loadhistory:deleted", onDeleted);
    s.on("loadhistory:updated", onUpdated);

    return () => {
      s.emit("unsubscribe:loadhistory");
      s.off("connect", subscribe);
      s.off("reconnect", subscribe);
      s.off("loadhistory:created", onCreated);
      s.off("loadhistory:reverted", onRevertedSocket);
      s.off("loadhistory:deleted", onDeleted);
      s.off("loadhistory:updated", onUpdated);
    };
  }, []);

  useEffect(() => {
    if (!showFilters) return;
    const onKey = (e) => e.key === "Escape" && setShowFilters(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showFilters]);

  const { clientOptions, carrierOptions } = useMemo(() => collectFilterOptions(histories), [histories]);

  const statusOptions = useMemo(
    () => [
      { value: "COMPLETED", label: t("status.completed") },
      { value: "PENDING", label: t("status.pending") },
      { value: "CANCELLED", label: t("status.cancelled") },
    ],
    [t, i18n.language]
  );

  const clientSelectOptions = useMemo(() => clientOptions.map((v) => ({ value: v, label: v })), [clientOptions]);

  const carrierSelectOptions = useMemo(
    () => carrierOptions.map((v) => ({ value: v, label: v })),
    [carrierOptions]
  );

  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  /* ---------- Search + Advanced filters ---------- */
  const filterHistories = (list, termRaw) => {
    const term = (termRaw || "").trim().toLowerCase();
    const f = activeFilters;

    return (list || []).filter((history) => {
      const load = history.load || {};

      // Search
      const loadIdStr = String(history.loadNumber ?? load.loadNumber ?? "").toLowerCase();
      const sessIdStr = String(history.transportSessionId ?? load.transportsessionId ?? "").toLowerCase();

      const pickupLocs = [
        String(history.pickupLocation || "").toLowerCase(),
        ...((load.pickupDeliveries || [])
          .filter((pd) => pd.type === "PICKUP")
          .map((pd) => String(pd.location || "").toLowerCase())),
      ].filter(Boolean);

      const deliveryLocs = [
        String(history.deliveryLocation || "").toLowerCase(),
        ...((load.pickupDeliveries || [])
          .filter((pd) => pd.type === "DELIVERY")
          .map((pd) => String(pd.location || "").toLowerCase())),
      ].filter(Boolean);

      const clientNames = [
        history?.client,
        load?.industryClient?.name,
        load?.industryClient?.companyName,
        load?.carrierClient?.name,
        load?.carrierClient?.companyName,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());

      const carrierNames = [
        load?.carrier?.name,
        load?.carrier?.companyName,
        load?.carrierName,
        history?.carrierCompanyName,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());

      const matchesSearch =
        !term ||
        loadIdStr.includes(term) ||
        sessIdStr.includes(term) ||
        pickupLocs.some((p) => p.includes(term)) ||
        deliveryLocs.some((d) => d.includes(term)) ||
        clientNames.some((c) => c.includes(term)) ||
        carrierNames.some((c) => c.includes(term)) ||
        String(history.truckPickupInfo || "").toLowerCase().includes(term) ||
        String(history.truckDeliveryInfo || "").toLowerCase().includes(term);

      if (!matchesSearch) return false;

      // Status
      const loadStatus = String(history.status || load.status || "").toUpperCase();
      if (f.status?.length && f.status[0] && loadStatus !== String(f.status[0]).toUpperCase()) return false;

      // Client
      if (f.clientNames?.length && f.clientNames[0]) {
        const selected = String(f.clientNames[0]).toLowerCase();
        const ok =
          clientNames.some((x) => x === selected) || String(history.client || "").toLowerCase() === selected;
        if (!ok) return false;
      }

      // Carrier
      if (f.carrierNames?.length && f.carrierNames[0]) {
        const selected = String(f.carrierNames[0]).toLowerCase();
        const ok = carrierNames.some((x) => x === selected);
        if (!ok) return false;
      }

      // IDs contains
      if (f.loadId && !loadIdStr.includes(String(f.loadId).toLowerCase())) return false;
      if (f.sessionId && !sessIdStr.includes(String(f.sessionId).toLowerCase())) return false;

      // Pickup/Delivery includes
      if (f.pickupIncludes) {
        const x = String(f.pickupIncludes).toLowerCase();
        if (!pickupLocs.some((p) => p.includes(x))) return false;
      }
      if (f.deliveryIncludes) {
        const x = String(f.deliveryIncludes).toLowerCase();
        if (!deliveryLocs.some((d) => d.includes(x))) return false;
      }

      // Date/Hour (snapshots)
      const snapInRange =
        (dateWithin(history.pickupDate, f.dateFrom, f.dateTo) && timeWithin(history.pickupHour, f.hourFrom, f.hourTo)) ||
        (dateWithin(history.deliveryDate, f.dateFrom, f.dateTo) && timeWithin(history.deliveryHour, f.hourFrom, f.hourTo));

      if ((f.dateFrom || f.dateTo || f.hourFrom || f.hourTo) && !snapInRange) return false;

      // Price + dims
      const priceValue =
        history.price != null && history.price !== "" ? Number(history.price) : Number(load.price);

      if (!inRange(priceValue, f.priceMin, f.priceMax)) return false;

      const L = Number(history.merchLength ?? load.length);
      const W = Number(history.merchWidth ?? load.width);
      const H = Number(history.merchHeight ?? load.height);

      if (!inRange(L, f.lengthMin, f.lengthMax)) return false;
      if (!inRange(W, f.widthMin, f.widthMax)) return false;
      if (!inRange(H, f.heightMin, f.heightMax)) return false;

      return true;
    });
  };

  // ✅ Compute visible histories
  const visible = useMemo(() => {
    return filterHistories(histories, searchTerm);
  }, [histories, searchTerm, activeFilters]);

  // ✅ Group visible histories by transport session
  const visibleSessions = useMemo(() => {
    return groupHistoriesBySession(visible);
  }, [visible]);

  const totalPages = Math.max(1, Math.ceil(visibleSessions.length / rowsPerPage));

  const pagedSessions = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return visibleSessions.slice(start, start + rowsPerPage);
  }, [visibleSessions, currentPage, rowsPerPage]);

  const pagedVisibleRowsCount = useMemo(() => {
    return pagedSessions.reduce((sum, session) => sum + session.rows.length, 0);
  }, [pagedSessions]);

  const paginationStart =
    visibleSessions.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;

  const paginationEnd = Math.min(currentPage * rowsPerPage, visibleSessions.length);

  const paginationPages = useMemo(() => {
    const pages = [];

    if (totalPages <= 7) {
      for (let p = 1; p <= totalPages; p += 1) pages.push(p);
      return pages;
    }

    pages.push(1);

    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let p = start; p <= end; p += 1) pages.push(p);

    if (currentPage < totalPages - 3) pages.push("...");

    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilters, histories.length, rowsPerPage]);

  useEffect(() => {
    if (typeof onVisibleCountChange === "function") {
      onVisibleCountChange(pagedVisibleRowsCount);
    }
  }, [onVisibleCountChange, pagedVisibleRowsCount]);

  useEffect(() => {
    return () => {
      if (typeof onVisibleCountChange === "function") {
        onVisibleCountChange(null);
      }
    };
  }, [onVisibleCountChange]);

  if (loading) return <div className="loading">{t("loading")}</div>;

  /* ---------- Export logic ---------- */
  const getRowsWithinDateRange = (rows, from, to) => {
    if (!from && !to) return rows;
    return rows.filter((row) => dateWithin(row.pickupDate, from, to) || dateWithin(row.deliveryDate, from, to));
  };

  const doExport = async ({ from, to, includeExtras, format }) => {
    const scope = visible;
    const ranged = getRowsWithinDateRange(scope, from, to);
    const flat = ranged.map((r) => flattenRowForExport(r, { includeExtras }));

    const stamp = (from ? formatDate(from) : "all") + "_to_" + (to ? formatDate(to) : "all");

    if (format === "csv") {
      const csv = toCSV(flat);
      download(`load-history_${stamp}.csv`, "text/csv;charset=utf-8;", csv);
      setShowExportModal(false);
      return;
    }

    if (format === "pdf") {
      try {
        const jsPDFmod = await import("jspdf");
        const autoTableMod = await import("jspdf-autotable");

        const jsPDF = jsPDFmod.default || jsPDFmod.jsPDF || jsPDFmod;
        const autoTable = autoTableMod.default || autoTableMod.autoTable || autoTableMod;

        const pdfRows = ranged.map((r) => flattenRowForPdf(r));

        if (!pdfRows.length) {
          alert(t("empty.noResults"));
          return;
        }

        const doc = new jsPDF({
          orientation: "landscape",
          unit: "pt",
          format: "a4",
        });

        const head = [[
          t("export.pdfHeaders.session"),
          t("export.pdfHeaders.load"),
          t("export.pdfHeaders.status"),
          t("export.pdfHeaders.client"),
          t("export.pdfHeaders.carrier"),
          t("export.pdfHeaders.price"),
          t("export.pdfHeaders.size"),
          t("export.pdfHeaders.pickup"),
          t("export.pdfHeaders.delivery"),
          t("export.pdfHeaders.stops"),
        ]];
        
        const body = pdfRows.map((r) => [
          r.session,
          r.load,
          r.status,
          r.client,
          r.carrier,
          r.price,
          r.size,
          r.pickup,
          r.delivery,
          r.stops,
        ]);

        doc.setFontSize(14);
        doc.text(t("export.pdfTitle"), 40, 30);

        doc.setFontSize(9);
        doc.text(
          `${t("export.dateFrom")}: ${from || "—"}   |   ${t("export.dateTo")}: ${to || "—"}`,
          40,
          46
        );

        autoTable(doc, {
          startY: 60,
          head,
          body,
          theme: "grid",
          margin: { top: 60, right: 24, bottom: 24, left: 24 },
          styles: {
            fontSize: 8,
            cellPadding: 6,
            overflow: "linebreak",
            valign: "middle",
            lineColor: [220, 228, 239],
            lineWidth: 0.4,
          },
          headStyles: {
            fillColor: [1, 157, 238],
            textColor: [255, 255, 255],
            fontSize: 8,
            fontStyle: "bold",
            halign: "left",
            valign: "middle",
          },
          bodyStyles: {
            textColor: [45, 55, 72],
          },
          alternateRowStyles: {
            fillColor: [247, 250, 252],
          },
          columnStyles: {
            0: { cellWidth: 62 },   // Session
            1: { cellWidth: 52 },   // Load
            2: { cellWidth: 62 },   // Status
            3: { cellWidth: 110 },  // Client
            4: { cellWidth: 110 },  // Carrier
            5: { cellWidth: 58, halign: "right" }, // Price
            6: { cellWidth: 72 },   // Size
            7: { cellWidth: 120 },  // Pickup
            8: { cellWidth: 120 },  // Delivery
            9: { cellWidth: 48, halign: "center" }, // Stops
          },
          didParseCell: (data) => {
            if (data.section === "body" && data.column.index === 2) {
              const value = String(data.cell.raw || "").toUpperCase();
              if (value === "COMPLETED") {
                data.cell.styles.textColor = [22, 163, 74];
                data.cell.styles.fontStyle = "bold";
              } else if (value === "CANCELLED") {
                data.cell.styles.textColor = [220, 38, 38];
                data.cell.styles.fontStyle = "bold";
              } else if (value === "PENDING") {
                data.cell.styles.textColor = [217, 119, 6];
                data.cell.styles.fontStyle = "bold";
              }
            }
          },
        });

        doc.save(`load-history_${stamp}.pdf`);
        setShowExportModal(false);
      } catch (e) {
        console.error(e);
        alert(t("errors.pdfMissingLibs"));
      }
      return;
    }

    try {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(flat);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "LoadHistory");
      XLSX.writeFile(wb, `load-history_${stamp}.xlsx`);
      setShowExportModal(false);
    } catch (e) {
      console.error(e);
      alert(t("errors.xlsxMissingLibs"));
    }
  };

  /* ---------- Ações ---------- */

  const showHistoryActionError = (error, fallbackKey) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.error;

    if (
      status === 409 &&
      code === "fiscal_history_modification_blocked"
    ) {
      alert(t("errors.fiscalModificationBlocked"));
      return;
    }

    if (
      status === 503 &&
      code === "fiscal_history_guard_unavailable"
    ) {
      alert(t("errors.fiscalGuardUnavailable"));
      return;
    }

    if (status === 429) {
      alert(t("errors.tooManyRequests"));
      return;
    }

    alert(t(fallbackKey));
  };

  const handleDelete = async (historyId) => {
    if (!window.confirm(t("confirm.delete"))) return;

    try {
      await axios.delete(`/load-history/${historyId}`);

      setHistories((prev) =>
        prev.filter((h) => h.id !== historyId)
      );

      if (selectedHistoryId === historyId) {
        setIsDetailsOpen(false);
        setSelectedHistory(null);
        setSelectedHistoryId(null);
      }
    } catch (error) {
      console.error("❌ Failed to delete history:", error);

      showHistoryActionError(
        error,
        "errors.deleteFailed"
      );
    }
  };

  const handleRevert = async (historyId) => {
    const confirmRevert = window.confirm(t("confirm.revert"));
    if (!confirmRevert) return;

    try {
      const { data } = await axios.post(
        `/load-history/${historyId}/revert`
      );

      setHistories((prev) =>
        prev.filter((h) => h.id !== historyId)
      );

      if (selectedHistoryId === historyId) {
        setIsDetailsOpen(false);
        setSelectedHistory(null);
        setSelectedHistoryId(null);
      }

      if (typeof onReverted === "function") {
        onReverted(data?.load || null);
      }
    } catch (error) {
      console.error("Failed to revert:", error);

      showHistoryActionError(
        error,
        "errors.revertFailed"
      );
    }
  };

  // Detalhes
  const openDetails = (row) => {
    setSelectedHistory(row);
    setSelectedHistoryId(row?.id || null);
    setIsDetailsOpen(true);
  };
  const closeDetails = () => {
    setIsDetailsOpen(false);
    setSelectedHistory(null);
    setSelectedHistoryId(null);
  };
  const handleDetailsReverted = (restoredLoad) => {
    if (selectedHistoryId != null) {
      setHistories((prev) => prev.filter((h) => h.id !== selectedHistoryId));
    }
    closeDetails();
    onReverted?.(restoredLoad);
  };

  /* ===================== Card helpers ===================== */

  const pickFirst = (arr = []) => (Array.isArray(arr) && arr.length ? arr[0] : null);

  const findPrimaryPlate = (pickupDetails, deliveryDetails, row) => {
    // Prefer pickup truck plate → delivery truck plate → snapshot fields
    const p = pickFirst(uniquePlates((pickupDetails || []).map((x) => x?.truckPlate).filter(Boolean)));
    const d = pickFirst(uniquePlates((deliveryDetails || []).map((x) => x?.truckPlate).filter(Boolean)));
    return onlyPlate(p || d || row?.truckPickupInfo || row?.truckDeliveryInfo || "—") || "—";
  };

  const statusClass = (s) => String(s || "").toLowerCase?.() || "unknown";

    const buildHistoryRowView = (row) => {
    const load = row.load || {};
    const hasPD = Array.isArray(load.pickupDeliveries) && load.pickupDeliveries.length > 0;

    const pickups = hasPD ? load.pickupDeliveries.filter((pd) => pd.type === "PICKUP") : [];
    const deliveries = hasPD ? load.pickupDeliveries.filter((pd) => pd.type === "DELIVERY") : [];

    const mapPointDetailFromPD = (pd) => {
      const isPickup = pd?.type === "PICKUP";

      const fallbackTruck = isPickup ? row?.truckPickupInfo : row?.truckDeliveryInfo;
      const fallbackTrailer = isPickup ? row?.trailerPickupInfo : row?.trailerDeliveryInfo;
      const fallbackDriver = isPickup ? row?.driverPickupInfo : row?.driverDeliveryInfo;

      return {
        location: pd?.location || "—",
        date: formatDate(pd?.date),
        dateDDMM: formatDDMM(pd?.date),
        hour: pd?.hour || "—",
        status: pd?.status || null,

        // 🆕 point-level dimensions
        length: pd?.length ?? null,
        width: pd?.width ?? null,
        height: pd?.height ?? null,
        dimensionsText: formatPointDimensions(pd),

        truckPlate: onlyPlate(pd?.truck?.plate || pd?.truckPlate || fallbackTruck) || "—",
        trailerPlate: pd?.trailer?.trailerNumber || pd?.trailerPlate || fallbackTrailer || "—",
        driverName: getDriverDisplayName(
          pd?.driver?.fullname,
          pd?.driver?.fullName,
          pd?.driver?.name,
          pd?.driver?.driverName,
          pd?.driverName,
          pd?.driverFullName,
          pd?.driverInfo,
          fallbackDriver
        ),

        driver2Name: getDriverDisplayName(
          pd?.secondDriver?.fullname,
          pd?.secondDriver?.fullName,
          pd?.secondDriver?.name,
          pd?.driver2Name
        ),

        driverNames: Array.isArray(pd?.driverNames)
          ? pd.driverNames
          : [
              getDriverDisplayName(
                pd?.driver?.fullname,
                pd?.driver?.fullName,
                pd?.driver?.name,
                pd?.driver?.driverName,
                pd?.driverName,
                pd?.driverFullName,
                pd?.driverInfo,
                fallbackDriver
              ),
              getDriverDisplayName(
                pd?.secondDriver?.fullname,
                pd?.secondDriver?.fullName,
                pd?.secondDriver?.name,
                pd?.driver2Name
              ),
            ].filter((name) => cleanDriverName(name)),
      };
    };

    const mapPointDetailFromList = (it) => ({
      location: it.location || "—",
      date: formatDate(it.date),
      dateDDMM: formatDDMM(it.date),
      hour: it.hour || "—",
      status: it.status || null,

      // 🆕 point-level dimensions
      length: it.length ?? null,
      width: it.width ?? null,
      height: it.height ?? null,
      dimensionsText: formatPointDimensions(it),

      truckPlate: it.truckPlate ? onlyPlate(it.truckPlate) : "—",
      trailerPlate: it.trailerPlate || "—",
      driverName: getDriverDisplayName(
        it.driverName,
        it.driverFullName,
        it.driverInfo,
        it.driver?.fullname,
        it.driver?.fullName,
        it.driver?.name
      ),

      driver2Name: getDriverDisplayName(
        it.driver2Name,
        it.secondDriver?.fullname,
        it.secondDriver?.fullName,
        it.secondDriver?.name
      ),

      driverNames: Array.isArray(it.driverNames)
        ? it.driverNames
        : [
            getDriverDisplayName(
              it.driverName,
              it.driverFullName,
              it.driverInfo,
              it.driver?.fullname,
              it.driver?.fullName,
              it.driver?.name
            ),
            getDriverDisplayName(
              it.driver2Name,
              it.secondDriver?.fullname,
              it.secondDriver?.fullName,
              it.secondDriver?.name
            ),
          ].filter((name) => cleanDriverName(name)),
    });

    const snapshotPickup = () =>
      !hasPD && row.pickupLocation
        ? [
            {
              location: row.pickupLocation,
              date: formatDate(row.pickupDate),
              dateDDMM: formatDDMM(row.pickupDate),
              hour: row.pickupHour || "—",
              status: null,

              // 🆕 fallback to load-level dimensions if point dimensions do not exist
              length: row.merchLength ?? load.length ?? null,
              width: row.merchWidth ?? load.width ?? null,
              height: row.merchHeight ?? load.height ?? null,
              dimensionsText: formatPointDimensions({
                length: row.merchLength ?? load.length ?? null,
                width: row.merchWidth ?? load.width ?? null,
                height: row.merchHeight ?? load.height ?? null,
              }),

              truckPlate: onlyPlate(row.truckPickupInfo) || "—",
              trailerPlate: row.trailerPickupInfo || "—",
              driverName: getDriverDisplayName(row.driverPickupInfo),
            },
          ]
        : [];

    const snapshotDelivery = () =>
      !hasPD && row.deliveryLocation
        ? [
            {
              location: row.deliveryLocation,
              date: formatDate(row.deliveryDate),
              dateDDMM: formatDDMM(row.deliveryDate),
              hour: row.deliveryHour || "—",
              status: null,

              // 🆕 fallback to load-level dimensions if point dimensions do not exist
              length: row.merchLength ?? load.length ?? null,
              width: row.merchWidth ?? load.width ?? null,
              height: row.merchHeight ?? load.height ?? null,
              dimensionsText: formatPointDimensions({
                length: row.merchLength ?? load.length ?? null,
                width: row.merchWidth ?? load.width ?? null,
                height: row.merchHeight ?? load.height ?? null,
              }),

              truckPlate: onlyPlate(row.truckDeliveryInfo) || "—",
              trailerPlate: row.trailerDeliveryInfo || "—",
              driverName: getDriverDisplayName(row.driverDeliveryInfo),
            },
          ]
        : [];

    const pickupDetails = hasPD
      ? pickups.map(mapPointDetailFromPD)
      : Array.isArray(row.pickupList) && row.pickupList.length
      ? row.pickupList.map(mapPointDetailFromList)
      : snapshotPickup();

    const deliveryDetails = hasPD
      ? deliveries.map(mapPointDetailFromPD)
      : Array.isArray(row.deliveryList) && row.deliveryList.length
      ? row.deliveryList.map(mapPointDetailFromList)
      : snapshotDelivery();

    const pickupFirst = pickupDetails?.[0] || null;
    const deliveryFirst = deliveryDetails?.[0] || null;

    const clientName =
      load.industryClient?.name ||
      load.industryClient?.companyName ||
      load.carrierClient?.name ||
      load.carrierClient?.companyName ||
      row.client ||
      "—";

    const carrierName =
      load.carrier?.name ||
      load.carrier?.companyName ||
      load.carrierName ||
      row.carrierCompanyName ||
      "—";

    const priceRaw =
      row.price != null && row.price !== ""
        ? row.price
        : typeof load.price === "number" || typeof load.price === "string"
        ? load.price
        : null;

    const priceNumber = priceRaw == null || priceRaw === "" ? null : Number(priceRaw);

    const priceText =
      priceNumber == null || Number.isNaN(priceNumber)
        ? "—"
        : new Intl.NumberFormat(i18n.language || "en", {
            style: "currency",
            currency: "EUR",
          }).format(priceNumber);

    const loadStatus = getHistoryStatus(row);

    const lengthText =
      row.merchLength != null && row.merchLength !== ""
        ? `${row.merchLength}m`
        : load.length != null && load.length !== ""
        ? `${load.length}m`
        : "—";

    const pickupDateTime = [
      pickupFirst?.date || formatDate(row.pickupDate),
      pickupFirst?.hour || row.pickupHour,
    ]
      .filter(Boolean)
      .join(" ");

    const deliveryDateTime = [
      deliveryFirst?.date || formatDate(row.deliveryDate),
      deliveryFirst?.hour || row.deliveryHour,
    ]
      .filter(Boolean)
      .join(" ");

    const finalDateTime = [
      formatDate(row.deliveryDate),
      row.deliveryHour,
    ]
      .filter(Boolean)
      .join(" ");

    const truckPlateSources =
      (pickupDetails?.length || deliveryDetails?.length)
        ? [
            ...(pickupDetails || []).map((x) => x?.truckPlate),
            ...(deliveryDetails || []).map((x) => x?.truckPlate),
          ]
        : [
            row?.truckPickupInfo,
            row?.truckDeliveryInfo,
          ];

    const allTruckPlates = uniqueNormalizedPlates(truckPlateSources);

    const trailerPlateSources =
      (pickupDetails?.length || deliveryDetails?.length)
        ? [
            ...(pickupDetails || []).map((x) => x?.trailerPlate),
            ...(deliveryDetails || []).map((x) => x?.trailerPlate),
          ]
        : [
            row?.trailerPickupInfo,
            row?.trailerDeliveryInfo,
          ];

    const allTrailerPlates = uniqueNormalizedPlates(trailerPlateSources);

    const allDriverNames = Array.from(
      new Set(
        [
          ...(pickupDetails || []).flatMap((x) =>
            Array.isArray(x?.driverNames)
              ? x.driverNames.map(cleanDriverName)
              : [cleanDriverName(x?.driverName), cleanDriverName(x?.driver2Name)]
          ),
          ...(deliveryDetails || []).flatMap((x) =>
            Array.isArray(x?.driverNames)
              ? x.driverNames.map(cleanDriverName)
              : [cleanDriverName(x?.driverName), cleanDriverName(x?.driver2Name)]
          ),
          cleanDriverName(row?.driverPickupInfo),
          cleanDriverName(row?.driverDeliveryInfo),
        ].filter(Boolean)
      )
    );

    return {
      loadNumber: row.loadNumber ?? load.loadNumber ?? "—",
      lengthText,

      truckPlate:
        allTruckPlates[0] ||
        normalizePlate(findPrimaryPlate(pickupDetails, deliveryDetails, row)) ||
        "—",
      truckExtraCount: Math.max(0, allTruckPlates.length - 1),

      trailerPlate:
        allTrailerPlates[0] ||
        onlyPlate(pickupFirst?.trailerPlate) ||
        onlyPlate(deliveryFirst?.trailerPlate) ||
        onlyPlate(row.trailerPickupInfo) ||
        onlyPlate(row.trailerDeliveryInfo) ||
        "—",
      trailerExtraCount: Math.max(0, allTrailerPlates.length - 1),

      driverName:
        allDriverNames[0] ||
        pickupFirst?.driverName ||
        deliveryFirst?.driverName ||
        row.driverPickupInfo ||
        row.driverDeliveryInfo ||
        "—",
      driverExtraCount: Math.max(0, allDriverNames.length - 1),

      pickupLocation: pickupFirst?.location || row.pickupLocation || "—",
      pickupDateTime: pickupDateTime || "—",
      pickupDimensions: pickupFirst?.dimensionsText || formatPointDimensions(pickupFirst),
      pickupExtraCount: Math.max(0, (pickupDetails?.length || 0) - 1),

      deliveryLocation: deliveryFirst?.location || row.deliveryLocation || "—",
      deliveryDateTime: deliveryDateTime || "—",
      deliveryDimensions: deliveryFirst?.dimensionsText || formatPointDimensions(deliveryFirst),
      deliveryExtraCount: Math.max(0, (deliveryDetails?.length || 0) - 1),

      clientName,
      carrierName,
      priceText,
      status: loadStatus,
      finalDateTime: finalDateTime || "—",
    };
  };

  /* ---------- UI ---------- */
  return (
    <>
      <div className="load-history-page">
        <div className="load-history-toolbar">
          <div className="load-history-actions">
            <button
              className="load-history-btn load-history-btn-ghost"
              onClick={() => {
                setDraftFilters({ ...activeFilters });
                setShowFilters((v) => !v);
              }}
              type="button"
            >
              {t("actions.addFilter")}
            </button>

            <button className="load-history-btn load-history-btn-primary" onClick={() => setShowExportModal(true)} type="button">
              {t("actions.export")}
            </button>
          </div>
        </div>

        <FilterBadges
          t={t}
          filters={activeFilters}
          formatStatusLabel={formatStatusLabel}
          onRemove={(key) => {
            const cleared = { ...activeFilters };
            if (Array.isArray(defaultFilters[key])) cleared[key] = [];
            else cleared[key] = defaultFilters[key] ?? "";
            setActiveFilters(cleared);
          }}
          onClearAll={() => setActiveFilters({ ...defaultFilters })}
        />

        {/* POPUP / DROPDOWN DE FILTROS */}
        {showFilters && (
          <div className="load-history-filter-modal-backdrop" onClick={() => setShowFilters(false)}>
            <div
              className="load-history-filter-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="load-history-filter-modal-header">
                <h3>{t("filters.title")}</h3>
                <button
                  className="load-history-filter-close"
                  onClick={() => setShowFilters(false)}
                  aria-label={t("filters.aria.close")}
                  type="button"
                >
                  ×
                </button>
              </div>

              <div className="load-history-filter-grid">
                {/* Status */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.status")}</label>
                  <Select
                    classNamePrefix="load-history-select"
                    styles={{
                      ...customSelectStyles,
                      menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                      menu: (b) => ({
                        ...b,
                        zIndex: 9999,
                        border: 0,
                        backgroundColor: "#0f1115",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                        marginTop: 6,
                        borderRadius: 12,
                        overflow: "hidden",
                      }),
                      menuList: (b) => ({
                        ...b,
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: 240,
                      }),
                    }}
                    menuPortalTarget={menuPortalTarget}
                    menuPosition="fixed"
                    options={statusOptions}
                    isClearable
                    placeholder={t("filters.placeholders.select")}
                    value={
                      draftFilters.status[0]
                        ? statusOptions.find((o) => o.value === draftFilters.status[0]) || null
                        : null
                    }
                    onChange={(opt) =>
                      setDraftFilters((df) => ({
                        ...df,
                        status: opt ? [opt.value] : [],
                      }))
                    }
                  />
                </div>

                {/* Client */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.client")}</label>
                  <Select
                    classNamePrefix="load-history-select"
                    styles={{
                      ...customSelectStyles,
                      menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                      menu: (b) => ({
                        ...b,
                        zIndex: 9999,
                        border: 0,
                        backgroundColor: "#0f1115",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                        marginTop: 6,
                        borderRadius: 12,
                        overflow: "hidden",
                      }),
                      menuList: (b) => ({
                        ...b,
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: 240,
                      }),
                    }}
                    menuPortalTarget={menuPortalTarget}
                    menuPosition="fixed"
                    options={clientSelectOptions}
                    isClearable
                    placeholder={t("filters.placeholders.select")}
                    value={
                      draftFilters.clientNames[0]
                        ? clientSelectOptions.find((o) => o.value === draftFilters.clientNames[0]) || null
                        : null
                    }
                    onChange={(opt) =>
                      setDraftFilters((df) => ({
                        ...df,
                        clientNames: opt ? [opt.value] : [],
                      }))
                    }
                  />
                </div>

                {/* Carrier */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.carrier")}</label>
                  <Select
                    classNamePrefix="load-history-select"
                    styles={{
                      ...customSelectStyles,
                      menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                      menu: (b) => ({
                        ...b,
                        zIndex: 9999,
                        border: 0,
                        backgroundColor: "#0f1115",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                        marginTop: 6,
                        borderRadius: 12,
                        overflow: "hidden",
                      }),
                      menuList: (b) => ({
                        ...b,
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: 240,
                      }),
                    }}
                    menuPortalTarget={menuPortalTarget}
                    menuPosition="fixed"
                    options={carrierSelectOptions}
                    isClearable
                    placeholder={t("filters.placeholders.select")}
                    value={
                      draftFilters.carrierNames[0]
                        ? carrierSelectOptions.find((o) => o.value === draftFilters.carrierNames[0]) || null
                        : null
                    }
                    onChange={(opt) =>
                      setDraftFilters((df) => ({
                        ...df,
                        carrierNames: opt ? [opt.value] : [],
                      }))
                    }
                  />
                </div>

                {/* Dates */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.dateFrom")}</label>
                  <input
                    type="date"
                    className="load-history-input"
                    value={draftFilters.dateFrom}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, dateFrom: e.target.value }))}
                  />
                </div>
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.dateTo")}</label>
                  <input
                    type="date"
                    className="load-history-input"
                    value={draftFilters.dateTo}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, dateTo: e.target.value }))}
                  />
                </div>

                {/* IDs */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.loadId")}</label>
                  <input
                    className="load-history-input"
                    placeholder={t("filters.placeholders.contains")}
                    value={draftFilters.loadId}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, loadId: e.target.value }))}
                  />
                </div>
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.sessionId")}</label>
                  <input
                    className="load-history-input"
                    placeholder={t("filters.placeholders.contains")}
                    value={draftFilters.sessionId}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, sessionId: e.target.value }))}
                  />
                </div>

                {/* Locations contains */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.pickupIncludes")}</label>
                  <input
                    className="load-history-input"
                    placeholder={t("filters.placeholders.pickupExample")}
                    value={draftFilters.pickupIncludes}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, pickupIncludes: e.target.value }))}
                  />
                </div>
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.deliveryIncludes")}</label>
                  <input
                    className="load-history-input"
                    placeholder={t("filters.placeholders.deliveryExample")}
                    value={draftFilters.deliveryIncludes}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, deliveryIncludes: e.target.value }))}
                  />
                </div>

                {/* Hours */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.hourFrom")}</label>
                  <input
                    type="time"
                    className="load-history-input"
                    value={draftFilters.hourFrom}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, hourFrom: e.target.value }))}
                  />
                </div>
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.hourTo")}</label>
                  <input
                    type="time"
                    className="load-history-input"
                    value={draftFilters.hourTo}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, hourTo: e.target.value }))}
                  />
                </div>

                {/* Price */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.priceMin")}</label>
                  <input
                    className="load-history-input"
                    type="number"
                    step="0.01"
                    value={draftFilters.priceMin}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, priceMin: e.target.value }))}
                  />
                </div>
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.priceMax")}</label>
                  <input
                    className="load-history-input"
                    type="number"
                    step="0.01"
                    value={draftFilters.priceMax}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, priceMax: e.target.value }))}
                  />
                </div>

                {/* Dimensions */}
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.lengthMin")}</label>
                  <input
                    className="load-history-input"
                    type="number"
                    value={draftFilters.lengthMin}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, lengthMin: e.target.value }))}
                  />
                </div>
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.lengthMax")}</label>
                  <input
                    className="load-history-input"
                    type="number"
                    value={draftFilters.lengthMax}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, lengthMax: e.target.value }))}
                  />
                </div>

                <div className="load-history-filter-field">
                  <label>{t("filters.fields.widthMin")}</label>
                  <input
                    className="load-history-input"
                    type="number"
                    value={draftFilters.widthMin}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, widthMin: e.target.value }))}
                  />
                </div>
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.widthMax")}</label>
                  <input
                    className="load-history-input"
                    type="number"
                    value={draftFilters.widthMax}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, widthMax: e.target.value }))}
                  />
                </div>

                <div className="load-history-filter-field">
                  <label>{t("filters.fields.heightMin")}</label>
                  <input
                    className="load-history-input"
                    type="number"
                    value={draftFilters.heightMin}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, heightMin: e.target.value }))}
                  />
                </div>
                <div className="load-history-filter-field">
                  <label>{t("filters.fields.heightMax")}</label>
                  <input
                    className="load-history-input"
                    type="number"
                    value={draftFilters.heightMax}
                    onChange={(e) => setDraftFilters((df) => ({ ...df, heightMax: e.target.value }))}
                  />
                </div>
              </div>

              <div className="load-history-filter-actions">
                <button className="load-history-btn" onClick={() => setDraftFilters({ ...defaultFilters })} type="button">
                  {t("actions.reset")}
                </button>
                <button className="load-history-btn" onClick={() => setShowFilters(false)} type="button">
                  {t("actions.cancel")}
                </button>
                <button
                  className="load-history-btn load-history-btn-primary"
                  onClick={() => {
                    setActiveFilters({ ...draftFilters });
                    setShowFilters(false);
                  }}
                  type="button"
                >
                  {t("actions.applyFilters")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* LISTA */}
        {visibleSessions.length === 0 ? (
          <p>{t("empty.noResults")}</p>
        ) : (
          <>
            <div className="load-history-session-list">
              {pagedSessions.map((session) => {
                const isCollapsed = !!collapsedSessions[session.key];
                const sessionStatusClass = statusClass(session.status);

                return (
                  <div
                    key={session.key}
                    className={`load-history-session-card lh-status-${sessionStatusClass}`}
                  >
                    <div className="load-history-session-header">
                      <div className="load-history-session-title-wrap">
                        <span className="load-history-session-title">
                          {t("labels.transportSession", "Sessão de Transporte")} #{session.transportSessionNumber}
                        </span>

                        <span className={`load-history-session-pill lh-status-${sessionStatusClass}`}>
                          {session.status === "COMPLETED" && (
                            <FaCheckCircle className="load-history-session-pill-icon" />
                          )}

                          {session.status === "CANCELLED" && (
                            <FaTimesCircle className="load-history-session-pill-icon" />
                          )}

                          {formatStatusLabel(session.status)}
                        </span>
                      </div>

                      <div className="load-history-session-progress-wrap">
                        <span className="load-history-session-progress-text">
                          {t("sessions.progress", {
                            completed: session.completedLoads,
                            total: session.totalLoads,
                          })}
                        </span>

                        <div className="load-history-session-progress-bar">
                          <span
                            className={`load-history-session-progress-fill lh-status-${sessionStatusClass}`}
                            style={{ width: `${session.progress}%` }}
                          />
                        </div>

                        <span className="load-history-session-progress-percent">
                          {session.progress}%
                        </span>
                      </div>

                      <button
                        className={`load-history-session-collapse ${
                          isCollapsed ? "is-collapsed" : "is-expanded"
                        }`}
                        type="button"
                        onClick={() => toggleSessionCollapse(session.key)}
                        aria-label={
                          isCollapsed
                            ? t("sessions.expandSession")
                            : t("sessions.collapseSession")
                        }
                      >
                        {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                      </button>
                    </div>

                    <div
                      className={`load-history-session-rows-wrap ${
                        isCollapsed ? "is-collapsed" : "is-expanded"
                      }`}
                    >
                      <div className="load-history-session-rows">
                        {session.rows.map((row) => {
                          const view = buildHistoryRowView(row);
                          const rowStatusClass = statusClass(view.status);

                          return (
                            <div
                              key={row.id}
                              className="load-history-session-row"
                              role="button"
                              tabIndex={isCollapsed ? -1 : 0}
                              onClick={() => {
                                if (!isCollapsed) openDetails(row);
                              }}
                              onKeyDown={(e) => {
                                if (!isCollapsed && e.key === "Enter") openDetails(row);
                              }}
                            >
                              <div className="load-history-row-load">
                                {view.loadNumber}
                              </div>

                              <div className="load-history-row-length">
                                {view.lengthText}
                              </div>

                              <div className="load-history-row-pill load-history-row-pill-truck">
                                <Truck className="load-history-row-lucide-icon" size={15} />
                                <span className="load-history-row-pill-text">{view.truckPlate}</span>

                                {view.truckExtraCount > 0 && (
                                  <span className="load-history-row-extra-badge">
                                    +{view.truckExtraCount}
                                  </span>
                                )}
                              </div>

                              <div className="load-history-row-pill load-history-row-pill-trailer">
                                <Container className="load-history-row-lucide-icon" size={15} />
                                <span className="load-history-row-pill-text">{view.trailerPlate}</span>

                                {view.trailerExtraCount > 0 && (
                                  <span className="load-history-row-extra-badge">
                                    +{view.trailerExtraCount}
                                  </span>
                                )}
                              </div>

                              <div className="load-history-row-pill load-history-row-pill-driver">
                                <User className="load-history-row-lucide-icon" size={15} />
                                <span className="load-history-row-pill-text">{view.driverName}</span>

                                {view.driverExtraCount > 0 && (
                                  <span className="load-history-row-extra-badge">
                                    +{view.driverExtraCount}
                                  </span>
                                )}
                              </div>

                              <div className="load-history-row-place">
                                <div className="load-history-row-place-title">
                                  <strong>{view.pickupLocation}</strong>

                                  {view.pickupExtraCount > 0 && (
                                    <span className="load-history-row-place-badge">
                                      +{view.pickupExtraCount}
                                    </span>
                                  )}
                                </div>

                                <span>{view.pickupDateTime}</span>
                              </div>

                              <div className="load-history-row-place">
                                <div className="load-history-row-place-title">
                                  <strong>{view.deliveryLocation}</strong>

                                  {view.deliveryExtraCount > 0 && (
                                    <span className="load-history-row-place-badge">
                                      +{view.deliveryExtraCount}
                                    </span>
                                  )}
                                </div>

                                <span>{view.deliveryDateTime}</span>
                              </div>

                              <div className="load-history-row-client">
                                {view.clientName}
                              </div>

                              <div className="load-history-row-carrier">
                                <Truck className="load-history-row-lucide-icon" size={14} />
                                <span>{view.carrierName}</span>
                              </div>

                              <div className="load-history-row-price">
                                {view.priceText}
                              </div>

                              <div className={`load-history-row-status lh-status-${rowStatusClass}`}>
                                {formatStatusLabel(view.status)}
                              </div>

                              <div className="load-history-row-finished">
                                {view.finalDateTime}
                              </div>

                              <div className="load-history-row-actions">
                                {row.billingFiscal?.verified === true && (
                                  <span
                                    title={
                                      row.billingFiscal.status === "ISSUED"
                                        ? `Documento fiscal #${row.billingFiscal.fiscalDocumentId} confirmado na reserva comercial`
                                        : row.billingFiscal.status === "AVAILABLE"
                                          ? "Aprovação comercial concluída; preparação fiscal ainda sujeita a validação"
                                          : row.billingFiscal.status === "ISSUING"
                                            ? "Reserva fiscal em processamento"
                                            : row.billingFiscal.status === "RECOVERY_REQUIRED"
                                              ? "Reserva fiscal requer reconciliação"
                                              : "Aprovação comercial pendente"
                                    }
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      minHeight: 25,
                                      padding: "4px 9px",
                                      borderRadius: 7,
                                      fontSize: 11,
                                      fontWeight: 650,
                                      letterSpacing: "0.01em",
                                      whiteSpace: "nowrap",
                                      border: "1px solid",
                                      borderColor:
                                        row.billingFiscal.status === "ISSUED"
                                          ? "rgba(52,211,153,0.28)"
                                          : row.billingFiscal.status === "RECOVERY_REQUIRED"
                                            ? "rgba(251,113,133,0.28)"
                                            : "rgba(148,163,184,0.24)",
                                      background:
                                        row.billingFiscal.status === "ISSUED"
                                          ? "rgba(52,211,153,0.10)"
                                          : row.billingFiscal.status === "RECOVERY_REQUIRED"
                                            ? "rgba(251,113,133,0.10)"
                                            : "rgba(148,163,184,0.08)",
                                      color:
                                        row.billingFiscal.status === "ISSUED"
                                          ? "#34d399"
                                          : row.billingFiscal.status === "RECOVERY_REQUIRED"
                                            ? "#fb7185"
                                            : "inherit",
                                    }}
                                  >
                                    {row.billingFiscal.status === "ISSUED"
                                      ? "Emitido"
                                      : row.billingFiscal.status === "ISSUING"
                                        ? "Em emissão"
                                        : row.billingFiscal.status === "RECOVERY_REQUIRED"
                                          ? "Reconciliação"
                                          : row.billingFiscal.status === "AVAILABLE"
                                            ? "Aprovado"
                                            : "Pendente"}
                                  </span>
                                )}

                                <BillingReviewAction
                                  history={row}
                                  compact
                                  onBillingChanged={refreshBillingState}
                                />

                                <button
                                  className="load-history-row-eye"
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDetails(row);
                                  }}
                                  title={t("tooltips.details")}
                                >
                                  <FaEye />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="load-history-pagination">
              <div className="load-history-pagination-pages">
                <button
                  className="load-history-page-btn"
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  aria-label={t("pagination.firstPage")}
                  title={t("pagination.firstPage")}
                >
                  |&lt;
                </button>

                <button
                  className="load-history-page-btn"
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-label={t("pagination.previousPage")}
                  title={t("pagination.previousPage")}
                >
                  &lt;
                </button>

                {paginationPages.map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`} className="load-history-page-ellipsis">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`load-history-page-btn ${
                        currentPage === page ? "active" : ""
                      }`}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  className="load-history-page-btn"
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-label={t("pagination.nextPage")}
                  title={t("pagination.nextPage")}
                >
                  &gt;
                </button>

                <button
                  className="load-history-page-btn"
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  aria-label={t("pagination.lastPage")}
                  title={t("pagination.lastPage")}
                >
                  &gt;|
                </button>
              </div>

              <div className="load-history-pagination-right">
                <select
                  className="load-history-page-size"
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                >
                  <option value={10}>{t("pagination.pageSize", { count: 10 })}</option>
                  <option value={20}>{t("pagination.pageSize", { count: 20 })}</option>
                  <option value={50}>{t("pagination.pageSize", { count: 50 })}</option>
                </select>

                <span className="load-history-page-range">
                  {t("pagination.range", {
                    start: paginationStart,
                    end: paginationEnd,
                    total: visibleSessions.length,
                  })}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal de Export */}
      <ExportModal
        t={t}
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={doExport}
        defaultFrom={activeFilters.dateFrom}
        defaultTo={activeFilters.dateTo}
      />

      {/* Painel de detalhes */}
      {isDetailsOpen && (
        <LoadHistoryDetails
          history={selectedHistory}
          historyId={selectedHistoryId}
          onClose={closeDetails}
          onReverted={handleDetailsReverted}
          onBillingChanged={refreshBillingState}
        />
      )}
    </>
  );
}

export default LoadHistory;
